<template>
  <nav class="navbar">
    <div class="navbar-left">
      <img src="@/assets/images/logo.png" alt="Logo" class="logo" />
      <span class="brand">Buzz Booking</span>
    </div>
    <ul class="navbar-center">
      <li><a class="nav-link" href="#who" @click.prevent="scrollTo('who')">Who It's For?</a></li>
      <li><a class="nav-link" href="#features" @click.prevent="scrollTo('features')">Features</a></li>
      <li><a class="nav-link" href="#buzz" @click.prevent="scrollTo('buzz')">Join Buzz</a></li>
      <li><a class="nav-link" href="#contact" @click.prevent="scrollTo('contact')">Contact Us</a></li>
      <li><a class="nav-link" href="/book">Book a Demo</a></li>
    </ul>
    <ul class="navbar-right">
      <li><a class="nav-login" href="/login">Login</a></li>
    </ul>
    <button
      class="navbar-hamburger"
      @click="mobileMenuOpen = !mobileMenuOpen"
      :aria-expanded="String(mobileMenuOpen)"
      aria-label="Toggle navigation"
    >
      <span></span>
      <span></span>
      <span></span>
    </button>
  </nav>
  <div class="mobile-menu" :class="{ 'mobile-menu--open': mobileMenuOpen }">
    <ul>
      <li><a href="#who" @click.prevent="scrollTo('who'); mobileMenuOpen = false">Who It's For?</a></li>
      <li><a href="#features" @click.prevent="scrollTo('features'); mobileMenuOpen = false">Features</a></li>
      <li><a href="#buzz" @click.prevent="scrollTo('buzz'); mobileMenuOpen = false">Join Buzz</a></li>
      <li><a href="#contact" @click.prevent="scrollTo('contact'); mobileMenuOpen = false">Contact Us</a></li>
      <li><a href="/book">Book a Demo</a></li>
      <li><a href="/login" class="mobile-login">Login</a></li>
    </ul>
  </div>
  <section class="hero">
    <div class="hero-inner">
      <div class="hero-content">
        <div class="hero-badge"><i class="fa-solid fa-bolt"></i> Smart Booking Software</div>
        <h1 class="hero-title">Simple scheduling.<br /><span>Powerful bookings.</span></h1>
        <p class="hero-sub">Manage bookings, staff and availability in one simple dashboard. Let clients book in seconds while you stay focused on what matters.</p>
        <div class="hero-ctas">
          <button class="btn-primary" @click.prevent="scrollTo('contact')">Get Started Free</button>
          <a class="btn-ghost" href="/book"><i class="fa-regular fa-calendar"></i> Book a Demo</a>
          <a class="btn-ghost" href="#who" @click.prevent="scrollTo('who')"><i class="fa-solid fa-circle-play"></i> Learn more</a>
        </div>
        <div class="hero-trust">
          <div class="trust-pill"><i class="fa-solid fa-bolt"></i> Next day deposits</div>
          <div class="trust-pill"><i class="fa-solid fa-headset"></i> 24/7 live support</div>
          <div class="trust-pill"><i class="fa-solid fa-shield-halved"></i> Secure payments</div>
        </div>
      </div>
      <div class="hero-image">
        <img src="@/assets/images/headline_img.svg" alt="Headline Image" />
      </div>
    </div>
  </section>
  <section id="who" class="who">
    <div class="who-inner">
      <div class="section-label">Who It's For</div>
      <h2 class="section-title">Built for businesses like yours</h2>
      <p class="section-sub">Whether you're a growing team or a solo business owner, Buzz helps you manage bookings and save time.</p>

      <div class="who-grid">
        <div class="who-card">
          <div class="slide slide1">
            <div class="who-icon"><i class="fa fa-solid fa-building"></i></div>
          </div>
          <div class="slide slide2">
            <h3>Small businesses</h3>
            <p>Accept bookings, manage staff, and keep schedules in one place.</p>
          </div>
        </div>

        <div class="who-card">
          <div class="slide slide1">
            <div class="who-icon"><i class="fa fa-solid fa-house-laptop"></i></div>
          </div>
          <div class="slide slide2">
            <h3>Tutors/Coaches</h3>
            <p>Let clients book in seconds so you can focus on your work.</p>
          </div>
        </div>

        <div class="who-card">
          <div class="slide slide1">
            <div class="who-icon"><i class="fa fa-solid fa-people-group"></i></div>
          </div>
          <div class="slide slide2">
            <h3>Teams</h3>
            <p>Coordinate availability and bookings across multiple team members.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
  <section id="features" class="features">
    <div class="features-inner">
      <div class="section-label">Features</div>
      <h2 class="section-title">Everything you need to grow</h2>
      <p class="section-sub">Key capabilities you can enable and customize for your business.</p>

      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-clock"></i></div>
          <h3>24/7 Support</h3>
          <p>Get help anytime you need it. Our support team is available around the clock to keep your bookings running
            smoothly.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-money-bill-transfer"></i></div>
          <h3>Next Day Funding</h3>
          <p>Receive payments from your bookings as soon as the next business day. No need to wait weeks for payouts.
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-user-plus"></i></div>
          <h3>Team Support</h3>
          <p>Manage your team members and their schedules with ease, ensuring smooth collaboration and efficient booking
            management.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-clipboard-user"></i></div>
          <h3>Customer Management</h3>
          <p>Keep track of your customers' information and booking history to provide personalized service and improve
            customer satisfaction.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-envelope"></i></div>
          <h3>Automated Emailing</h3>
          <p>Send automatic email reminders and notifications to keep your customers informed and reduce no-shows.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-credit-card"></i></div>
          <h3>Online Payments</h3>
          <p>Accept payments securely online, making it easy for your customers to pay and for you to manage
            transactions.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-link"></i></div>
          <h3>Booking Link</h3>
          <p>Provide a simple, shareable link for customers to book appointments directly.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-handshake"></i></div>
          <h3>Website Integration</h3>
          <p>Integrate booking functionality into your existing website for a consistent customer experience.</p>
        </div>

        <div class="feature-card">
          <div class="feature-icon"><i class="fa-solid fa-paintbrush"></i></div>
          <h3>Customizable</h3>
          <p>Tailor the booking experience to match your brand and business needs with customizable options. Create your
            own services and booking flows to suit your unique requirements.</p>
        </div>
      </div>
    </div>
  </section>
  <section id="buzz" class="buzz">
    <div class="buzz-inner">
      <div class="buzz-image">
        <img src="@/assets/images/Buzz-Network-Image.svg" alt="Buzz preview" />
      </div>

      <div class="buzz-content">
        <div class="section-label">Join Buzz</div>
        <h2 class="section-title">Join Buzz Financial</h2>
        <p class="section-sub">No additional fees, no hidden fees, no surprises. Straight-forward pricing that gives you everything.</p>
        <div class="buzz-perks">
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> 24/7 Support</div>
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> Next Day Funding</div>
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> Simple Fees</div>
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> Fast Deposits</div>
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> Business Funding</div>
          <div class="buzz-perk"><i class="fa-solid fa-circle-check"></i> Marketing &amp; Coaching</div>
        </div>
        <button class="btn-primary" @click.prevent="scrollTo('contact')">Join Buzz Now</button>
      </div>
    </div>
  </section>
  <section id="contact" class="contact">
    <div class="contact-inner">
      <div class="contact-form">
        <h2>Contact Us</h2>
        <p>Have a question or want to work with us? Send a message and we'll get back to you shortly.</p>
        <form @submit.prevent="submitForm" method="post">
          <div class="form-row">
            <label for="name">Name*</label>
            <input id="name" name="name" type="text" placeholder="Your name" v-model="cust_name" @input="onNameInput" :class="{ 'input-error': attemptedSubmit && !nameValid }" />
          </div>

          <div class="form-row">
            <label for="email">Email*</label>
            <input id="email" name="email" type="email" placeholder="you@example.com" v-model="cust_email" :class="{ 'input-error': attemptedSubmit && !emailValid }" />
          </div>

          <div class="form-row">
            <label for="phone">Phone</label>
            <input id="phone" name="phone" type="tel" placeholder="(123) 456-7890" v-model="cust_phone" @input="onPhoneInput" maxlength="14" inputmode="tel" :class="{ 'input-error': attemptedSubmit && !phoneValid }" />
          </div>

          <div class="form-row">
            <label for="message">Message</label>
            <textarea id="message" name="message" rows="5" placeholder="How can we help?" maxlength="250" v-model="cust_message" :class="{ 'input-error': attemptedSubmit && !messageValid }"></textarea>
            <div class="char-counter">{{ messageCount }} / 250</div>
          </div>

          <div class="form-row">
            <button type="submit" class="btn-primary">Send Message</button>
          </div>
          <div v-if="error" class="error-message">{{ error }}</div>
          <div v-if="success" class="success-message">{{ success }}</div>
        </form>
      </div>

      <div class="contact-image">
        <img src="@/assets/images/Buzz-Contact-Us2.svg" alt="Contact image" />
      </div>
    </div>
  </section>
  <footer class="site-footer">
    <div class="footer-inner">
      <div class="footer-brand">
        <img src="@/assets/images/logo.png" alt="Buzz logo" />
        <div>
          <strong>Buzz Booking</strong>
          <div class="small">Powered by Buzz Financial</div>
        </div>
      </div>

      <div class="footer-socials">
        <div class="small">Follow us on social media</div>
        <div class="social-icons">
          <a href="https://www.facebook.com/people/Buzz-Financial/61575255470646/" target="_blank" rel="noopener" aria-label="Facebook">
            <i class="fa-brands fa-facebook fa-2x"></i>
          </a>
          <a href="https://x.com/buzzfinancia1" target="_blank" rel="noopener" aria-label="Twitter">
            <i class="fa-brands fa-x-twitter fa-2x"></i>
          </a>
          <a href="https://www.instagram.com/buzzfinancial/" target="_blank" rel="noopener" aria-label="Instagram">
            <i class="fa-brands fa-instagram fa-2x"></i>
          </a>
          <a href="https://www.linkedin.com/company/buzz-financial/" target="_blank" rel="noopener" aria-label="LinkedIn">
            <i class="fa-brands fa-linkedin fa-2x"></i>
          </a>
        </div>
      </div>

      <div class="footer-policies">
        <a href="/privacy-policy">Privacy Policy</a>
        <a href="/terms-of-service">Terms of Service</a>
        <a href="/sms-terms">SMS Terms</a>
      </div>

      <div class="footer-contact">
        <div class="small">Visit the Buzz Financial website</div>
        <a href="https://buzz.financial/">Buzz Financial</a>
      </div>
    </div>
  </footer>
</template>

<script setup>

  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import http from '@/lib/http';
  // import { useSessionStore } from '@/stores/session';

  // const session = useSessionStore();

  function scrollTo(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const navbar = document.querySelector('.navbar');
    const navbarHeight = navbar ? navbar.offsetHeight : 0;
    const rectTop = el.getBoundingClientRect().top + window.pageYOffset;
    const offset = Math.max(0, rectTop - navbarHeight);
    window.scrollTo({ top: offset, behavior: 'smooth' });
  }

  // Animate who cards when the section scrolls into view
  let whoObserver = null;
  onMounted(() => {
    const whoSection = document.getElementById('who');
    if (!whoSection) return;
    whoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          whoSection.classList.add('in-view');
        } else {
          whoSection.classList.remove('in-view');
        }
      });
    }, { threshold: 0.25 });
    whoObserver.observe(whoSection);
    // session.clear();
  });

  onBeforeUnmount(() => {
    if (whoObserver) whoObserver.disconnect();
  });

  const mobileMenuOpen = ref(false);

  const cust_name = ref('');
  const cust_email = ref('');
  const cust_phone = ref('');
  const cust_message = ref('');
  const error = ref('');
  const success = ref('');
  const messageCount = computed(() => cust_message.value.length);
  const attemptedSubmit = ref(false);

  const nameValid = computed(() => cust_name.value.trim() !== '' && !/\d/.test(cust_name.value));
  const emailValid = computed(() => validateEmail(cust_email.value));
  const phoneValid = computed(() => cust_phone.value.trim() === '' || validatePhone(cust_phone.value));
  // message is optional
  const messageValid = computed(() => true);

  function validateEmail (email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  }

  function validatePhone (phone) {
    const re = /^\(?([0-9]{3})\)?[\s-.●]?([0-9]{3})[\s-.●]?([0-9]{4})$/;
    return re.test(String(phone));
  }

  const isFormValid = computed(() => {
    return nameValid.value && emailValid.value && phoneValid.value && messageValid.value;
  });

  async function submitForm () {
    attemptedSubmit.value = true;
    if (!isFormValid.value) {
      error.value = 'Please fill out all required fields with valid information.';
      return;
    }
    error.value = '';

    try {
      const payload = {
        name: cust_name.value,
        email: cust_email.value,
        phone: cust_phone.value,
        message: cust_message.value
      };
      await http.post('/customers/contactUs', payload);
      cust_name.value = '';
      cust_email.value = '';
      cust_phone.value = '';
      cust_message.value = '';
      attemptedSubmit.value = false;
      success.value = 'Your message has been sent successfully!';
    } catch (err) {
      error.value = 'An error occurred while sending your message. Please try again later.';
    }
  }

  function onNameInput (e) {
    const value = e.target.value;
    const cleaned = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ'’ .-\s]/g, '');
    cust_name.value = cleaned;
  }

  function onPhoneInput (e) {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    let formatted = '';
    if (digits.length === 0) {
      formatted = '';
    } else if (digits.length <= 3) {
      formatted = `(${digits}`;
    } else if (digits.length <= 6) {
      formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    } else {
      formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    cust_phone.value = formatted;
  }

</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&family=Montserrat:wght@700;800&display=swap');

/* ── Navbar ─────────────────────────────────────────────────────────────── */
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 2rem;
  background: rgba(15, 33, 51, 0.97);
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: saturate(140%) blur(8px);
  font-size: 1rem;
  font-weight: 500;
}

.navbar-center {
  list-style: none;
  display: flex;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
}

.navbar-center li a {
  color: #c8d6e0;
  text-decoration: none;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-family: 'Noto Sans', sans-serif;
}

/* Nav link hover + entrance animations */
.nav-link {
  color: #c8d6e0;
  text-decoration: none;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  display: inline-block;
  transition: color 180ms ease, background 180ms ease, transform 150ms ease;
}
.nav-link::after {
  content: '';
  display: block;
  height: 2px;
  width: 0%;
  background: linear-gradient(90deg, #ff9d00, #ffc95d);
  border-radius: 2px;
  transition: width 240ms ease;
  margin-top: 4px;
}
.nav-link:hover {
  color: #ff9d00;
  background: rgba(255,157,0,0.07);
  transform: translateY(-2px);
}
.nav-link:hover::after { width: 100%; }

.navbar-center li { opacity: 0; transform: translateY(-6px); }
.navbar-center li:nth-child(1) { animation: navFade 320ms ease forwards 80ms; }
.navbar-center li:nth-child(2) { animation: navFade 320ms ease forwards 150ms; }
.navbar-center li:nth-child(3) { animation: navFade 320ms ease forwards 220ms; }
.navbar-center li:nth-child(4) { animation: navFade 320ms ease forwards 290ms; }

@keyframes navFade {
  to { opacity: 1; transform: translateY(0); }
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.logo {
  height: 46px;
  width: auto;
}

.brand {
  font-weight: 700;
  font-size: 1.05rem;
  color: #e8edf2;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: -0.01em;
}

.navbar-right {
  list-style: none;
  display: flex;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  align-items: center;
}

.nav-login {
  color: #0f2133;
  background: linear-gradient(135deg, #ff9d00, #ffb940);
  text-decoration: none;
  padding: 0.45rem 1.2rem;
  border-radius: 20px;
  font-family: 'Noto Sans', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  position: relative;
  transition: transform 150ms ease, box-shadow 200ms ease, background 200ms ease;
  opacity: 0;
  animation: loginEntrance 400ms cubic-bezier(.2,.9,.2,1) forwards 380ms;
  box-shadow: 0 4px 14px rgba(255,157,0,0.3);
}
.nav-login:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255,157,0,0.42);
  background: linear-gradient(135deg, #ffad1a, #ffc95d);
}

@keyframes loginEntrance {
  to { opacity: 1; }
}

/* ── Hamburger ──────────────────────────────────────────────────────────── */
.navbar-hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 38px;
  height: 38px;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 7px;
  transition: background 0.15s;
  z-index: 1002;
  flex-shrink: 0;
}
.navbar-hamburger:hover { background: rgba(255,255,255,0.08); }
.navbar-hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: #c8d6e0;
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.2s ease;
  transform-origin: center;
}
.navbar-hamburger[aria-expanded="true"] span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.navbar-hamburger[aria-expanded="true"] span:nth-child(2) { opacity: 0; transform: scaleX(0); }
.navbar-hamburger[aria-expanded="true"] span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ── Mobile menu ────────────────────────────────────────────────────────── */
.mobile-menu {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding-top: 74px;
  background: rgba(10, 22, 35, 0.98);
  backdrop-filter: saturate(120%) blur(8px);
  z-index: 998;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.32s ease, padding-bottom 0.32s ease;
}
.mobile-menu.mobile-menu--open {
  max-height: 520px;
  padding-bottom: 16px;
}
.mobile-menu ul { list-style: none; margin: 0; padding: 0; }
.mobile-menu ul li a {
  display: block;
  padding: 14px 24px;
  color: #c8d6e0;
  text-decoration: none;
  font-family: 'Noto Sans', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  transition: color 0.15s, background 0.15s, padding-left 0.15s;
}
.mobile-menu ul li a:hover {
  color: #ff9d00;
  background: rgba(255,157,0,0.05);
  padding-left: 32px;
}
.mobile-login {
  color: #ff9d00 !important;
  font-weight: 700 !important;
  border-bottom: none !important;
}

@media (max-width: 768px) {
  .navbar-center, .navbar-right { display: none; }
  .navbar-hamburger { display: flex; }
  .mobile-menu { display: block; }
  .navbar { padding: 0.85rem 1.25rem; }
}

@media (max-width: 600px) {
  .brand { font-size: 0.95rem; }
}

/* ── Hero ───────────────────────────────────────────────────────────────── */
.hero {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5.5rem 2rem 5rem;
  background: linear-gradient(135deg, #f8faff 0%, #fff9ed 55%, #fffbf0 100%);
  position: relative;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  top: -80px;
  right: -120px;
  width: 560px;
  height: 560px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,193,59,0.12) 0%, transparent 70%);
  pointer-events: none;
}
.hero::after {
  content: '';
  position: absolute;
  bottom: -60px;
  left: -80px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(44,115,223,0.07) 0%, transparent 70%);
  pointer-events: none;
}

.hero-inner {
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 3rem;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.hero-content { flex: 1 1 500px; text-align: left; }

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(255,157,0,0.12), rgba(255,193,93,0.15));
  border: 1px solid rgba(255,157,0,0.3);
  color: #c47600;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: 'Noto Sans', sans-serif;
  padding: 5px 12px;
  border-radius: 20px;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hero-title {
  font-size: 3.4rem;
  margin: 0 0 1rem 0;
  color: #0f2133;
  line-height: 1.1;
  font-weight: 800;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: -0.02em;
}
.hero-title span {
  background: linear-gradient(90deg, #ff9d00, #e07500);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-sub {
  margin: 0 0 1.75rem 0;
  color: #4a5a6b;
  font-size: 1.15rem;
  font-family: 'Noto Sans', sans-serif;
  max-width: 50ch;
  line-height: 1.6;
}

.hero-ctas {
  display: flex;
  gap: 0.85rem;
  align-items: center;
  margin-top: 0.25rem;
  flex-wrap: wrap;
}

.hero-trust {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.75rem;
}
.trust-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.38rem 0.85rem;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 999px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #334155;
  box-shadow: 0 1px 4px rgba(15,33,51,0.06);
}
.trust-pill i {
  color: #ff9d00;
  font-size: 0.72rem;
}

.hero-image {
  flex: 0 0 460px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.hero-image img {
  width: 100%;
  max-width: 460px;
  height: auto;
  display: block;
  filter: drop-shadow(0 16px 40px rgba(15,33,51,0.12));
}

/* Shared buttons */
.btn-primary {
  background: linear-gradient(135deg, #ff9d00, #ffb330);
  color: #0f2133;
  border: none;
  padding: 0.75rem 1.6rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  box-shadow: 0 6px 22px rgba(255,157,0,0.32);
  font-family: 'Noto Sans', sans-serif;
  transition: background 0.25s ease, transform 0.18s ease, box-shadow 0.25s ease;
  letter-spacing: 0.01em;
}
.btn-primary:hover {
  background: linear-gradient(135deg, #ffad1a, #ffc043);
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(255,157,0,0.4);
}
.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(255,157,0,0.28);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #0f2133;
  text-decoration: none;
  padding: 0.72rem 1.4rem;
  border-radius: 10px;
  border: 1.5px solid #d1d8e0;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: 'Noto Sans', sans-serif;
  transition: border-color 0.18s, background 0.18s, transform 0.15s;
  background: rgba(255,255,255,0.7);
}
.btn-ghost:hover {
  border-color: #ff9d00;
  background: rgba(255,157,0,0.06);
  transform: translateY(-2px);
}

@media (max-width: 900px) {
  .hero-image { flex: 0 0 340px; }
  .hero-inner { gap: 2rem; }
}

@media (max-width: 600px) {
  .hero { padding: 3rem 1.25rem 3.5rem; }
  .hero-inner { flex-direction: column; gap: 0; align-items: flex-start; }
  .hero-content { text-align: left; }
  .hero-title { font-size: 2rem; margin-bottom: 0.75rem; line-height: 1.15; }
  .hero-sub { font-size: 1rem; margin-bottom: 1.25rem; max-width: 100%; }
  .hero-ctas { gap: 0.6rem; }
  .btn-primary, .btn-ghost { width: 100%; text-align: center; justify-content: center; }
  .hero-image { display: none; }
  .hero-trust { flex-wrap: wrap; }
}

/* ── Section shared ─────────────────────────────────────────────────────── */
.section-label {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #ff9d00;
  font-family: 'Noto Sans', sans-serif;
  margin-bottom: 0.5rem;
}
.section-title {
  font-size: 2.25rem;
  font-weight: 800;
  font-family: 'Montserrat', sans-serif;
  color: #0f2133;
  margin: 0 0 0.6rem 0;
  letter-spacing: -0.02em;
}
.section-sub {
  color: #5a6a7a;
  font-size: 1.05rem;
  font-family: 'Noto Sans', sans-serif;
  max-width: 60ch;
  margin: 0 auto 2rem;
  line-height: 1.65;
}

/* ── Who section ────────────────────────────────────────────────────────── */
.who {
  padding: 5rem 2rem;
  background: #ffffff;
}
.who-inner {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  padding: 0 12px;
}

.who-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  margin-top: 1.5rem;
}

.who-card {
  background: #fff;
  border-radius: 16px;
  text-align: center;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  border: 1.5px solid #f0f4f8;
  box-shadow: 0 4px 20px rgba(15,33,51,0.05);
  transition: box-shadow 0.25s, border-color 0.25s, transform 0.25s;
}
.who-card:hover {
  box-shadow: 0 12px 36px rgba(15,33,51,0.1);
  border-color: #ffd085;
  transform: translateY(-4px);
}

.who-icon {
  width: 100%;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  background: linear-gradient(135deg, #fff8e6, #fff1c2);
  color: #e07500;
  flex-shrink: 0;
}
.who-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.who-icon .fa { font-size: 3rem; color: #e07500; }

.who-card .slide {
  width: 100%;
  max-width: 100%;
  transition: 0.6s cubic-bezier(.2,.9,.2,1);
}
.who-card .slide.slide1 {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transform: translateY(60px);
  opacity: 0;
}
.who.in-view .who-card .slide.slide1 {
  transform: translateY(0);
  opacity: 1;
  transition-delay: 100ms;
}
.who-card .slide.slide2 {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px 20px 20px;
  box-sizing: border-box;
  flex-direction: column;
  background: #ffffff;
  transform: translateY(-60px);
  opacity: 0;
  flex: 1;
}
.who.in-view .who-card .slide.slide2 {
  transform: translateY(0);
  opacity: 1;
  transition-delay: 200ms;
}

.who-card h3 {
  margin: 0 0 0.4rem 0;
  font-size: 1rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  color: #0f2133;
  text-align: center;
}
.who-card p {
  margin: 0;
  color: #5a6a7a;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.92rem;
  text-align: center;
  line-height: 1.5;
}

@media (max-width: 900px) {
  .who-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .who { padding: 3.5rem 1.25rem; }
  .who-grid { grid-template-columns: 1fr; }
}

/* ── Features section ───────────────────────────────────────────────────── */
.features {
  padding: 5rem 2rem;
  background: linear-gradient(160deg, #f8faff 0%, #fffbf0 100%);
}
.features-inner {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  padding: 0 12px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.feature-card {
  background: #ffffff;
  padding: 1.5rem 1.4rem;
  border-radius: 14px;
  border: 1.5px solid #eef1f6;
  box-shadow: 0 4px 16px rgba(15,33,51,0.05);
  text-align: left;
  min-height: 175px;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  position: relative;
  overflow: hidden;
}
.feature-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff9d00, #ffc95d, #ffad1a);
  background-size: 200% 100%;
  animation: gradientShift 3.5s linear infinite;
  opacity: 0;
  transition: opacity 0.25s;
}
.feature-card:hover::before { opacity: 1; }
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(15,33,51,0.1);
  border-color: #ffd085;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.feature-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(135deg, #fff4d6, #ffe8a0);
  color: #c47600;
  font-size: 1.1rem;
  margin-bottom: 0.8rem;
  transition: background 0.25s, color 0.25s, transform 0.25s;
}
.feature-card:hover .feature-icon {
  background: linear-gradient(135deg, #ff9d00, #ffb330);
  color: #ffffff;
  transform: scale(1.08);
}
.feature-card h3 {
  margin: 0 0 0.4rem 0;
  font-size: 0.97rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  color: #0f2133;
}
.feature-card p {
  margin: 0;
  color: #5a6a7a;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
}

@media (max-width: 900px) {
  .features-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .features { padding: 3.5rem 1.25rem; }
  .features-grid { grid-template-columns: 1fr; }
}

/* ── Buzz / Join section ────────────────────────────────────────────────── */
.buzz {
  padding: 5rem 2rem;
  background: #ffffff;
}
.buzz-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 3rem;
  padding: 0 12px;
}
.buzz-image {
  flex: 0 0 500px;
  display: flex;
  justify-content: center;
}
.buzz-image img {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(15,33,51,0.1);
}
.buzz-content {
  flex: 1;
  text-align: left;
  font-family: 'Noto Sans', sans-serif;
}
.buzz-content .section-title { text-align: left; }
.buzz-content .section-sub { text-align: left; margin-left: 0; font-size: 1rem; color: #4a5a6b; margin-bottom: 1.4rem; }

.buzz-perks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1.2rem;
  margin-bottom: 2rem;
}
.buzz-perk {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.93rem;
  color: #334155;
  font-weight: 500;
}
.buzz-perk i {
  color: #ff9d00;
  font-size: 0.8rem;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .buzz-inner { flex-direction: column; gap: 2rem; }
  .buzz-image { order: -1; width: 100%; max-width: 520px; flex: none; }
  .buzz-content { text-align: center; }
  .buzz-content .section-title, .buzz-content .section-sub { text-align: center; margin-left: auto; }
  .buzz-perks { justify-items: start; max-width: 300px; margin: 0 auto 2rem; }
}
@media (max-width: 600px) {
  .buzz { padding: 3.5rem 1.25rem; }
  .buzz-image { display: none; }
  .buzz-perks { grid-template-columns: 1fr; }
}

/* ── Contact section ────────────────────────────────────────────────────── */
.contact {
  padding: 5rem 2rem;
  background: linear-gradient(160deg, #f8faff 0%, #fffbf0 100%);
}
.contact-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 3rem;
  align-items: center;
  padding: 0 12px;
}
.contact-form {
  flex: 1 1 480px;
  font-family: 'Noto Sans', sans-serif;
  background: #fff;
  border-radius: 18px;
  padding: 2.25rem 2rem;
  border: 1.5px solid #eef1f6;
  box-shadow: 0 8px 32px rgba(15,33,51,0.07);
}
.contact-form h2 {
  font-size: 1.6rem;
  margin: 0 0 0.4rem 0;
  color: #0f2133;
  font-family: 'Montserrat', sans-serif;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.contact-form > p {
  color: #64748b;
  margin-bottom: 1.5rem;
  font-size: 0.93rem;
  line-height: 1.55;
}
.contact-form form {
  display: grid;
  gap: 0.85rem;
}
.form-row label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.form-row input,
.form-row textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.9rem;
  border-radius: 9px;
  border: 1.5px solid #e2e8f0;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.92rem;
  color: #1e293b;
  background: #f8fafc;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}
.form-row input::placeholder,
.form-row textarea::placeholder {
  color: #94a3b8;
}
.form-row input:focus,
.form-row textarea:focus {
  border-color: #ff9d00;
  box-shadow: 0 0 0 3px rgba(255,157,0,0.12);
  background: #fff;
}
.form-row textarea { resize: vertical; }

.contact-image {
  flex: 0 0 400px;
  display: flex;
  justify-content: center;
}
.contact-image img {
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 14px;
  filter: drop-shadow(0 12px 32px rgba(15,33,51,0.1));
}

.error-message {
  color: #e53e3e;
  font-size: 0.87rem;
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 5px;
}
.success-message {
  color: #16a34a;
  font-size: 0.87rem;
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
}
.char-counter {
  text-align: right;
  font-size: 0.8rem;
  color: #94a3b8;
  margin-top: 0.25rem;
}
.input-error {
  border-color: #e53e3e !important;
  box-shadow: 0 0 0 3px rgba(229,62,62,0.1) !important;
}

@media (max-width: 900px) {
  .contact-inner { flex-direction: column; text-align: center; }
  .contact-image { width: 100%; max-width: 480px; flex: none; }
  .contact-form { width: 100%; flex: none; }
}
@media (max-width: 600px) {
  .contact { padding: 3rem 1.25rem; }
  .contact-form { padding: 1.5rem 1.25rem; }
  .contact-image { display: none; }
}

/* ── Footer ─────────────────────────────────────────────────────────────── */
.site-footer {
  background: #0a1828;
  color: #c8d6e0;
  padding: 2.5rem 2rem;
  border-top: 1px solid rgba(255,255,255,0.06);
}
.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 0 12px;
  flex-wrap: wrap;
}
.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.footer-brand img { height: 40px; width: auto; }
.footer-brand strong { font-family: 'Montserrat', sans-serif; font-size: 1rem; color: #e8edf2; }
.footer-brand .small { font-size: 0.8rem; color: #7a8fa0; margin-top: 2px; }

.footer-socials {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.footer-socials .small { color: #7a8fa0; font-size: 0.8rem; }

.social-icons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.footer-socials a {
  color: #8fa3b4;
  transition: color 200ms ease, background 200ms ease, transform 150ms ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.08);
}
.footer-socials a:hover {
  color: #ff9d00;
  background: rgba(255,157,0,0.1);
  border-color: rgba(255,157,0,0.3);
  transform: translateY(-2px);
}
.footer-socials a i { font-size: 18px; }

.footer-policies {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.footer-policies .small { color: #7a8fa0; font-size: 0.8rem; }
.footer-policies a {
  color: #a0b4c4;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: color 180ms ease, transform 150ms ease;
  position: relative;
}
.footer-policies a::after {
  content: '';
  position: absolute;
  left: 0; bottom: -3px;
  width: 0; height: 2px;
  border-radius: 2px;
  background: #ff9d00;
  transition: width 200ms ease;
}
.footer-policies a:hover { color: #ff9d00; transform: translateY(-1px); }
.footer-policies a:hover::after { width: 100%; }

.footer-contact { text-align: right; }
.footer-contact .small { color: #7a8fa0; font-size: 0.8rem; margin-bottom: 4px; display: block; }
.footer-contact a {
  color: #ff9d00;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  transition: color 180ms;
}
.footer-contact a:hover { color: #ffc043; }

@media (max-width: 900px) {
  .footer-inner { flex-direction: column; text-align: center; gap: 1.25rem; }
  .footer-contact { text-align: center; }
  .footer-brand { flex-direction: column; text-align: center; }
}
</style>
